import { useCallback } from 'react';
import { gql } from 'graphql-request';
import useSWRMutation from 'swr/mutation';
import { basketRequest, OutOfStockError } from '@hooks/basket/_utils';
import { track } from '@repo/ui/src/services/analytics/utils';
import { useCustomerIdToken } from '@hooks/customer';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { checkoutStarted } from '@repo/ui/src/services/analytics';
import useBasket from '@hooks/basket/useBasket';
import { GraphQLRequestWithTokenProps } from '@repo/ui/src/utils/graphql-request';
import {
  gcToAnalytics,
  basketLineInputToAnalytics,
} from '@repo/ui/src/services/analytics/factories';

const SESSION_BRIDGE_QUERY = gql`
  mutation CreateSessionBridge {
    createSessionBridge
  }
`;

export const verifyBasket = gql`
  query verifyBasket {
    verifyBasket(siteId: "") {
      items
    }
  }
`;

interface Arg {
  skipVerification?: boolean;
  skipSession?: boolean;
}
const fetcher = async (
  props: GraphQLRequestWithTokenProps,
  {
    arg: { skipVerification = false, skipSession = false } = {},
  }: {
    arg: Arg;
  },
) => {
  if (!skipVerification) {
    const outOfStock = await basketRequest({ ...props, document: verifyBasket });
    if (outOfStock.verifyBasket.items?.length) {
      throw new OutOfStockError(outOfStock.verifyBasket.items);
    }
    return outOfStock;
  }
  if (!skipSession) {
    return basketRequest(props);
  }
};

const useGoToCheckout = () => {
  const { currency, locale } = useCurrentUrl();
  const { data: basket } = useBasket();
  const idToken = useCustomerIdToken();

  const { trigger, ...swrValues } = useSWRMutation(
    { document: SESSION_BRIDGE_QUERY, locale, idToken },
    fetcher,
  );

  const goToCheckout = useCallback(
    async ({ skipVerification, skipSession }: Arg = {}) => {
      const result = await trigger({
        skipVerification: skipVerification || !basket?.lineItems?.length,
        skipSession,
      });
      result.createSessionBridge?.forEach((cookie: string) => {
        document.cookie = cookie;
      });
      track(checkoutStarted, {
        affiliation: 'website',
        currency,
        locale,
        brand: 'Traeger',
        basket_token: basket?.basketJWT,
        products: [
          ...(basket?.lineItems?.map(basketLineInputToAnalytics) ?? []),
          ...(basket?.giftCards?.map(gcToAnalytics) ?? []),
        ],
        checkout_id: basket?.id ?? '',
      });
      return result;
    },
    [trigger, basket, locale, currency],
  );
  return { trigger: goToCheckout, ...swrValues };
};

export default useGoToCheckout;
